import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useTimers } from "@src/appV2/lib/utils/useTimers";
import { useCallback, useMemo } from "react";

import { type TimekeepingAction } from "../constants";

export type TimekeepingActionEventType = "start" | "success" | "error" | "non-ip-conversion";

function buildTimerKey(action: TimekeepingAction, shiftId: string): string {
  return `timekeeping-action-${action}-${shiftId}`;
}

interface TrackTimekeepingActionCompletedProps {
  nonInstantPayReason?: string;
}

interface TrackTimekeepingActionFailedProps {
  error: string;
}

interface UseTimekeepingActionTimerResult {
  startTimekeepingActionTracking: () => void;
  completeTimekeepingActionTracking: (props?: TrackTimekeepingActionCompletedProps) => void;
  failTimekeepingActionTracking: (props: TrackTimekeepingActionFailedProps) => void;
}

interface UseTimekeepingActionTimerParams {
  action: TimekeepingAction;
  shiftId: string;
  workerId: string;
  workplaceId: string | undefined;
  timekeepingMethod: string;
}

export function useTimekeepingActionTimer(
  params: UseTimekeepingActionTimerParams
): UseTimekeepingActionTimerResult {
  const { action, shiftId, workerId, workplaceId, timekeepingMethod } = params;
  const { start, stop, isRunning } = useTimers();
  const timerKey = buildTimerKey(action, shiftId);

  const basePayload = useMemo(
    () => ({ action, shiftId, workerId, workplaceId, timekeepingMethod }),
    [action, shiftId, workerId, workplaceId, timekeepingMethod]
  );

  const startTimekeepingActionTracking = useCallback(() => {
    if (isRunning(timerKey)) {
      return;
    }

    start(timerKey);

    logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_ACTION, {
      ...basePayload,
      eventType: "start" as TimekeepingActionEventType,
    });
  }, [isRunning, start, timerKey, basePayload]);

  const completeTimekeepingActionTracking = useCallback(
    (props?: TrackTimekeepingActionCompletedProps) => {
      const duration = stop(timerKey);

      if (!isDefined(duration)) {
        return;
      }

      const eventType: TimekeepingActionEventType = isDefined(props?.nonInstantPayReason)
        ? "non-ip-conversion"
        : "success";

      logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_ACTION_COMPLETED, {
        ...basePayload,
        durationMs: duration.totalDurationMs,
        activeDurationMs: duration.activeDurationMs,
        ...props,
      });

      logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_ACTION, {
        ...basePayload,
        eventType,
        durationMs: duration.totalDurationMs,
        activeDurationMs: duration.activeDurationMs,
        ...(isDefined(props?.nonInstantPayReason) && {
          nonInstantPayReason: props.nonInstantPayReason,
        }),
      });
    },
    [stop, timerKey, basePayload]
  );

  const failTimekeepingActionTracking = useCallback(
    (props: TrackTimekeepingActionFailedProps) => {
      const duration = stop(timerKey);

      logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_ACTION, {
        ...basePayload,
        eventType: "error" as TimekeepingActionEventType,
        error: props.error,
        ...(isDefined(duration) && {
          durationMs: duration.totalDurationMs,
          activeDurationMs: duration.activeDurationMs,
        }),
      });
    },
    [stop, timerKey, basePayload]
  );

  return {
    startTimekeepingActionTracking,
    completeTimekeepingActionTracking,
    failTimekeepingActionTracking,
  };
}
